type Offer = { id: string; name: string; interval: 'year' | 'month'; unit_amount: number; data_gb: number };
type Selection = {
  email: string;
  items: Array<{ iccid: string }>;
  planId: string;
  prepayYears: number;
  offer: Offer;
};
type Progress = { orderId?: string; token?: string };
type Request = (path: string, method?: string, body?: unknown, token?: string) => Promise<any>;

// Saved browser progress is a convenience, not a lock on another activation.
export async function prepayHandoff(existing: Progress | undefined, selection: Selection, api: Request) {
  if (existing?.orderId && existing.token) {
    let order;
    try {
      order = await api(`/orders/${existing.orderId}`, 'GET', undefined, existing.token);
    } catch (error) {
      if ((error as Error & { status?: number }).status !== 404) throw error;
    }
    const sameAccount = order?.email?.trim().toLowerCase() === selection.email.trim().toLowerCase();
    if (order?.kind === 'prepaid' && sameAccount) {
      if (order.status === 'draft') return { ...existing, resumeOnly: false };
      const sameSims = order.items.length === selection.items.length &&
        order.items.every((sim: { iccid: string }) => selection.items.some(item => item.iccid === sim.iccid));
      const sameOffer = matchingOffer(order.plan, selection.offer) && order.prepay_years === selection.prepayYears;
      const paid = ['activating', 'complete', 'needs_attention', 'resolved',
        'needs_support', 'refund_pending', 'refunded'].includes(order.status);
      if (sameSims && (paid || (sameOffer && ['checkout', 'creating_checkout'].includes(order.status))))
        return { ...existing, resumeOnly: true };
    }
  }
  const created = await api('/prepay/orders', 'POST', {});
  return { orderId: created.order.id as string, token: created.access_token as string, resumeOnly: false };
}

function matchingOffer(plan: Offer | null, selected: Offer) {
  return plan?.interval === 'year' && plan.name === selected.name &&
    plan.unit_amount === selected.unit_amount && plan.data_gb === selected.data_gb;
}

export async function savePrepaySelection(progress: Progress, selection: Selection, api: Request, referral = '') {
  const path = `/orders/${progress.orderId}`;
  const body = { email: selection.email.trim(), region: 'uk', items: selection.items,
    prepay_years: selection.prepayYears, referral };
  // Prepaid orders have their own server-owned offer IDs (including older
  // single-SIM prices). Resolve the equivalent offer instead of copying a group ID.
  const available = await api(path, 'PUT', body, progress.token);
  const plan = available.plans.find((candidate: Offer) => matchingOffer(candidate, selection.offer));
  if (!plan) throw new Error('The price or plan has changed. Please return to Choose plan and try again.');
  return api(path, 'PUT', { ...body, plan_id: plan.id }, progress.token);
}
